import { Check, KeyRound, PackageX } from 'lucide-react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useOutletContext, useParams, useSearchParams } from 'react-router';
import { EntityFacts, EntityHead, RelatedSection } from '@/components/entity-sheet/EntityParts';
import { EntitySheet } from '@/components/entity-sheet/EntitySheet';
import { Button } from '@/components/ui/button';
import { Skeleton, SkeletonRegion } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { apiPlace, sectionPath } from '../place/place';
import { useRoleText } from '../roles/role-text';
import { StateMessage } from '../ui/StateMessage';
import { moduleIcon, useModuleText } from './module-text';
import { HistoryList, MODULE_TONE, ModuleLine, type ModulesContext } from './ModulesPage';
import { useRoleCatalog, type ModuleState } from './use-modules';

/**
 * A module's card (decision 101): its state here and since when, the act this caller may use, the
 * roles that belong to it (linking to «Ruoli e accessi»), and its own history here. It reads from
 * the list it was opened from, as the invitation's card does.
 */
export function ModuleSheet() {
  const { t } = useTranslation('administration');
  const context = useOutletContext<ModulesContext>();
  const { place, items, loading } = context;
  const { moduleId = '' } = useParams();
  const [params] = useSearchParams();
  const titleId = useId();
  const { name } = useModuleText();
  const query = params.size > 0 ? `?${params.toString()}` : '';
  const listPath = sectionPath(place, 'modules');
  const back = `${listPath}${query}`;
  const index = items.findIndex((item) => item.module.id === moduleId);
  const item = index >= 0 ? items[index] : undefined;
  const neighbour = (other: ModuleState | undefined, key: 'previous' | 'next') =>
    other === undefined
      ? null
      : {
          to: `${listPath}/${other.module.id}${query}`,
          label: t(`moduleSection.card.${key}`, { module: name(other.module) }),
        };

  return (
    <EntitySheet
      titleId={titleId}
      back={back}
      backLabel={t('moduleSection.card.back')}
      closeLabel={t('moduleSection.card.close')}
      previous={neighbour(items[index - 1], 'previous')}
      next={index >= 0 ? neighbour(items[index + 1], 'next') : null}
    >
      {item !== undefined ? (
        <ModuleCard item={item} titleId={titleId} context={context} />
      ) : (
        <GoneOrLoading loading={loading} titleId={titleId} back={back} />
      )}
    </EntitySheet>
  );
}

function GoneOrLoading({
  loading,
  titleId,
  back,
}: {
  readonly loading: boolean;
  readonly titleId: string;
  readonly back: string;
}) {
  const { t } = useTranslation('administration');
  if (loading) {
    return (
      <div>
        <h2 id={titleId} className="sr-only">
          {t('common.loading')}
        </h2>
        <SkeletonRegion label={t('common.loading')} className="flex flex-col gap-3">
          <Skeleton className="h-12 w-60" />
          <Skeleton className="h-32 w-full" />
        </SkeletonRegion>
      </div>
    );
  }
  return (
    <div>
      <h2 id={titleId} className="sr-only">
        {t('moduleSection.card.gone.title')}
      </h2>
      <StateMessage
        role="alert"
        icon={PackageX}
        title={t('moduleSection.card.gone.title')}
        text={t('moduleSection.card.gone.text')}
        action={
          <Button asChild variant="outline">
            <Link to={back}>{t('moduleSection.card.back')}</Link>
          </Button>
        }
      />
    </div>
  );
}

function CardAction({
  item,
  acts,
}: {
  readonly item: ModuleState;
  readonly acts: ModulesContext['acts'];
}) {
  const { t } = useTranslation('administration');
  const pending = acts.isPending(item);
  if (item.can.activate) {
    return (
      <Button size="sm" disabled={pending} onClick={() => void acts.activate(item)}>
        <Check />
        {t('moduleSection.activate')}
      </Button>
    );
  }
  if (item.can.deactivate) {
    return (
      <Button
        size="sm"
        variant="outline"
        className="text-destructive"
        disabled={pending}
        onClick={() => void acts.deactivate(item)}
      >
        {t('moduleSection.deactivate')}
      </Button>
    );
  }
  return undefined;
}

function CardHistory({
  titleId,
  entries,
  placeId,
}: {
  readonly titleId: string;
  readonly entries: ModulesContext['history'];
  readonly placeId: string;
}) {
  const { t } = useTranslation('administration');
  return (
    <section aria-labelledby={`${titleId}-history`} className="entity-section">
      <h3 id={`${titleId}-history`} className="entity-section-title">
        {t('moduleSection.card.history')}
      </h3>
      {entries.length === 0 ? (
        <p className="text-muted-foreground text-sm">{t('moduleSection.history.empty')}</p>
      ) : (
        <HistoryList entries={entries} hereId={placeId} />
      )}
    </section>
  );
}

function ModuleCard({
  item,
  titleId,
  context,
}: {
  readonly item: ModuleState;
  readonly titleId: string;
  readonly context: ModulesContext;
}) {
  const { t } = useTranslation('administration');
  const { name, description } = useModuleText();
  const role = useRoleText();
  const { place, detail, history, acts } = context;
  const pair = apiPlace(place);
  const placeId = pair?.placeId ?? '';
  const catalog = useRoleCatalog(pair?.placeKind ?? 'organizations', placeId);
  const roles = (catalog ?? []).filter((entry) => entry.module?.id === item.module.id);
  const ownHistory = history.filter((entry) => entry.module.id === item.module.id);
  const rolesPath = sectionPath(place, 'roles');
  const Icon = moduleIcon(item.module.code);

  return (
    <div className="flex flex-col gap-4">
      <EntityHead
        titleId={titleId}
        kind={t('moduleSection.card.kind')}
        title={name(item.module)}
        sub={description(item.module)}
        lead={
          <span aria-hidden="true" className="module-card-icon">
            <Icon className="size-5" />
          </span>
        }
        status={
          <span className={cn('status-badge', MODULE_TONE[item.state])}>
            {t(`moduleSection.state.${item.state}`)}
          </span>
        }
        actions={<CardAction item={item} acts={acts} />}
      />
      <EntityFacts
        facts={[
          { label: t('moduleSection.card.facts.state'), value: <ModuleLine item={item} /> },
          { label: t('moduleSection.card.facts.where'), value: detail.place.name },
        ]}
      />
      {catalog === null ? null : (
        <RelatedSection
          id={`${titleId}-roles`}
          title={t('moduleSection.card.roles')}
          count={roles.length}
          rows={roles.map((entry) => ({
            key: entry.id,
            to: `${rolesPath}?role=${encodeURIComponent(entry.id)}`,
            label: role.name(entry),
            sub: role.description(entry),
            lead: (
              <KeyRound aria-hidden="true" className="text-muted-foreground size-4.5 flex-none" />
            ),
          }))}
          more={{ to: rolesPath, label: t('moduleSection.card.rolesMore') }}
        />
      )}
      <CardHistory titleId={titleId} entries={ownHistory} placeId={placeId} />
    </div>
  );
}
